import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import {
    getFormattedAmount,
    getFormattedPercentage,
    formatSmartPrice,
} from "Shared/Utils";
import { Collapse, Typography, Button, Divider, Tag } from "antd";
import {
    HistoryOutlined,
    MessageOutlined,
    LineChartOutlined,
} from "@ant-design/icons";
import BinanceTransactions from "Components/Drawers/BinanceTransactions";
import MarketView from "Components/Drawers/MarketView";
import Axios from "axios";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { Tokens } from "Shared/Tokens";
import { getCoinIcon } from "utils/CoinIconCache";
import { getCoinsList, getCoinInfo } from "utils/CoinGecko";

const { Panel } = Collapse;
const { Title, Text } = Typography;

const CANONICAL_COINGECKO_IDS = {
    BTC: "bitcoin",
    ETH: "ethereum",
    BNB: "binancecoin",
    SOL: "solana",
    XRP: "ripple",
    USDT: "tether",
    ADA: "cardano",
    DOGE: "dogecoin",
    DOT: "polkadot",
    AVAX: "avalanche-2",
    LINK: "chainlink",
    MATIC: "matic-network",
    UNI: "uniswap",
    ATOM: "cosmos",
    LTC: "litecoin",
    TRX: "tron",
    SUI: "sui",
    ARB: "arbitrum",
    OP: "optimism",
    ONDO: "ondo-finance",
    FET: "fetch-ai",
    NEAR: "near",
    PEPE: "pepe",
};

const AssetInWallet = (props) => {
    const history = useHistory();
    const [color, setColor] = useState({
        primary: Tokens.textSecondary,
        sentiment: "neutral",
    });
    const [transactionsDrawerVisible, setTransactionsDrawerVisible] =
        useState(false);
    const [transactions, setTransactions] = useState([]);
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [transactionsPage, setTransactionsPage] = useState(1);
    const [transactionsPagination, setTransactionsPagination] = useState({
        total: 0,
        page: 1,
        limit: 20,
        totalPages: 0,
        hasMore: false,
    });
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [coinLogo, setCoinLogo] = useState(null);
    const [showMarketDrawer, setShowMarketDrawer] = useState(false);
    const [marketCoinInfo, setMarketCoinInfo] = useState({});
    const [marketLoading, setMarketLoading] = useState(false);

    const {
        asset = {
            name: "",
            currentValue: 0,
        },
        index = 0,
        total = 0,
        timeRange = 30,
        currency = "USDT",
        cutoffDate = "2026-01-01",
    } = props;

    const isMobile = windowWidth < 768;
    const isTablet = windowWidth >= 768 && windowWidth < 1024;

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const fetchCoinLogo = async () => {
            const assetName = asset.name || asset.asset;
            if (!assetName) {
                setCoinLogo(null);
                return;
            }
            const iconUrl = await getCoinIcon(assetName);
            setCoinLogo(iconUrl);
        };

        fetchCoinLogo();
    }, [asset.name, asset.asset]);

    useEffect(() => {
        const roi = asset.roi?.percentage || 0;
        if (roi > 0) {
            setColor({
                primary: Tokens.positive,
                sentiment: "positive",
            });
        } else if (roi < 0) {
            setColor({
                primary: Tokens.negative,
                sentiment: "negative",
            });
        } else {
            setColor({
                primary: Tokens.textSecondary,
                sentiment: "neutral",
            });
        }
    }, [asset]);

    const [activeDays, setActiveDays] = useState(
        cutoffDate && cutoffDate !== "all" ? "all" : timeRange
    );

    const fetchTransactions = async (page = 1, daysOverride = null) => {
        const assetSymbol = asset.name || asset.asset;
        if (!assetSymbol) return;

        const daysToUse =
            daysOverride !== null ? daysOverride : activeDays || timeRange;
        if (daysOverride !== null) {
            setActiveDays(daysOverride);
        }

        setTransactionsLoading(true);
        try {
            const api = `${process.env.REACT_APP_API}/binance/transactions/${assetSymbol}`;
            const response = await Axios.get(api, {
                params: {
                    days: daysToUse === "all" ? "all" : daysToUse,
                    page,
                    limit: 20,
                    cutoffDate,
                },
                withCredentials: true,
            });

            if (response.data?.success && response.data?.data) {
                const transactionsData = response.data.data.transactions || [];
                setTransactions(transactionsData);
                setTransactionsPagination(
                    response.data.data.pagination || {
                        total: 0,
                        page: 1,
                        limit: 20,
                        totalPages: 0,
                        hasMore: false,
                    }
                );
            } else {
                setTransactions([]);
            }
        } catch (error) {
            setTransactions([]);
        } finally {
            setTransactionsLoading(false);
        }
    };

    const handleShowTransactions = () => {
        if (!transactionsDrawerVisible) {
            setTransactionsDrawerVisible(true);
            fetchTransactions(1);
        } else {
            setTransactionsDrawerVisible(false);
        }
    };

    const handlePageChange = (page) => {
        setTransactionsPage(page);
        fetchTransactions(page);
    };

    const handleShowMarketStats = async () => {
        const assetName = asset.name || asset.asset;
        if (!assetName) return;
        setMarketLoading(true);
        try {
            const coinsList = await getCoinsList();
            const lowerSymbol = assetName.toLowerCase();
            const canonicalId =
                CANONICAL_COINGECKO_IDS[assetName.toUpperCase()];
            const matches = (Array.isArray(coinsList) ? coinsList : [])
                .filter((c) => (c.symbol || "").toLowerCase() === lowerSymbol)
                .sort((a, b) => (a.id || "").length - (b.id || "").length);
            const matchedCoin = matches.find((c) => c.id === canonicalId) ||
                matches[0] || {
                    id: canonicalId || lowerSymbol,
                    symbol: lowerSymbol,
                    name: assetName,
                };
            const infoList = await getCoinInfo(
                [matchedCoin],
                (currency || "USDT").toLowerCase()
            );
            setMarketCoinInfo(
                infoList?.[0] || {
                    symbol: lowerSymbol,
                    name: assetName,
                }
            );
            setShowMarketDrawer(true);
        } catch (err) {
            setMarketCoinInfo({ symbol: assetName, name: assetName });
            setShowMarketDrawer(true);
        } finally {
            setMarketLoading(false);
        }
    };

    const roiVal = asset.roi?.percentage || 0;
    const roiDollarValue = Number.isFinite(Number(asset.roi?.value))
        ? Number(asset.roi.value)
        : (asset.currentValue || 0) - (asset.invested || 0);
    const roiDollarSign = roiDollarValue >= 0 ? "+" : "-";
    const isPositiveRoi = roiVal >= 0;
    const symbol = asset.name === "USDT" ? "USDT" : asset.name || asset.asset;
    let portfolioSharePct = 0;
    if (Number(asset.holdings) > 0) {
        portfolioSharePct = Number(asset.holdings);
    } else if (Number(total) > 0) {
        portfolioSharePct =
            ((Number(asset.currentValue) || 0) / Number(total)) * 100;
    }

    const getHeader = () => {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    padding: "4px 0",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                    }}
                >
                    {coinLogo ? (
                        <img
                            src={coinLogo}
                            alt={symbol}
                            style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                backgroundColor: Tokens.raised,
                            }}
                            onError={(e) => {
                                e.target.style.display = "none";
                            }}
                        />
                    ) : (
                        <div
                            style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                backgroundColor: Tokens.raised,
                                border: `1px solid ${Tokens.hairline}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: Tokens.textSecondary,
                                fontWeight: 500,
                                fontSize: "12px",
                                fontFamily: Tokens.fontMono,
                            }}
                        >
                            {symbol.slice(0, 3)}
                        </div>
                    )}
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "2px",
                        }}
                    >
                        <Text
                            style={{
                                color: Tokens.textPrimary,
                                fontWeight: 500,
                                fontSize: "15px",
                            }}
                        >
                            {symbol}
                        </Text>
                        <Text
                            style={{
                                color: Tokens.textMuted,
                                fontSize: "12px",
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {getFormattedAmount(asset.quantity || 0, false, 4)}{" "}
                            {symbol}
                            {Number(asset.currentPrice) > 0 && (
                                <span
                                    data-testid={`asset-live-price-${symbol}`}
                                    style={{ color: Tokens.textSecondary }}
                                >
                                    {" "}
                                    · {getCurrencySymbol(currency)}
                                    {formatSmartPrice(asset.currentPrice)}
                                </span>
                            )}
                        </Text>
                    </div>
                </div>

                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: "4px",
                    }}
                >
                    <Text
                        style={{
                            color: Tokens.textPrimary,
                            fontWeight: 500,
                            fontSize: "15px",
                            fontVariantNumeric: "tabular-nums",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {getCurrencySymbol(currency)}
                        {getFormattedAmount(asset.currentValue || 0, true, 2)}
                    </Text>
                    {asset.roi && asset.name !== "USDT" ? (
                        <Tag
                            style={{
                                backgroundColor: isPositiveRoi
                                    ? Tokens.positiveBg
                                    : Tokens.negativeBg,
                                color: isPositiveRoi
                                    ? Tokens.positive
                                    : Tokens.negative,
                                border: `1px solid ${
                                    isPositiveRoi
                                        ? Tokens.positiveBorder
                                        : Tokens.negativeBorder
                                }`,
                                borderRadius: Tokens.radiusTag,
                                padding: "0 6px",
                                fontSize: "11px",
                                fontWeight: 500,
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                                margin: 0,
                            }}
                        >
                            {getFormattedPercentage(roiVal, 1)}
                        </Tag>
                    ) : (
                        <Text
                            style={{
                                color: Tokens.textMuted,
                                fontSize: "11px",
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            @{getCurrencySymbol(currency)}
                            {formatSmartPrice(asset.currentPrice || 0)}
                        </Text>
                    )}
                </div>
            </div>
        );
    };

    const allocationBadges = [
        asset.allocation?.spot > 0 && `Spot ${asset.allocation.spot}%`,
        asset.allocation?.earn > 0 && `Earn ${asset.allocation.earn}%`,
        asset.allocation?.funding > 0 && `Funding ${asset.allocation.funding}%`,
        asset.allocation?.margin > 0 && `Margin ${asset.allocation.margin}%`,
    ].filter(Boolean);

    return (
        <Collapse
            style={{
                backgroundColor: Tokens.card,
                border: `1px solid ${Tokens.hairline}`,
                borderRadius: Tokens.radiusCard,
                marginBottom: "8px",
                overflow: "hidden",
            }}
        >
            <Panel
                header={getHeader()}
                key={index}
                style={{
                    backgroundColor: Tokens.card,
                    borderBottom: "none",
                }}
            >
                <div
                    style={{
                        background: Tokens.bg,
                        padding: "16px",
                        borderRadius: Tokens.radiusControl,
                        border: `1px solid ${Tokens.hairline}`,
                    }}
                >
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "16px",
                            marginBottom: "12px",
                        }}
                    >
                        <div>
                            <Text
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Invested
                            </Text>
                            <Title
                                level={4}
                                style={{
                                    color: Tokens.textPrimary,
                                    margin: "4px 0 0",
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {getCurrencySymbol(currency)}
                                {getFormattedAmount(
                                    asset.invested || 0,
                                    true,
                                    2
                                )}
                            </Title>
                        </div>
                        <div style={{ textAlign: "right" }}>
                            <Text
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Current value
                            </Text>
                            <Title
                                level={4}
                                style={{
                                    color: Tokens.textPrimary,
                                    margin: "4px 0 0",
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {getCurrencySymbol(currency)}
                                {getFormattedAmount(
                                    asset.currentValue || 0,
                                    true,
                                    2
                                )}
                            </Title>
                        </div>
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "16px",
                            marginBottom: "12px",
                        }}
                    >
                        <div>
                            <Text
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Net P&amp;L
                            </Text>
                            <Title
                                level={4}
                                style={{
                                    color: color?.primary,
                                    margin: "4px 0 0",
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {roiDollarSign}
                                {getCurrencySymbol(currency)}
                                {getFormattedAmount(
                                    Math.abs(roiDollarValue),
                                    true,
                                    2
                                )}{" "}
                                <span
                                    style={{
                                        fontSize: "12px",
                                        fontFamily: Tokens.fontMono,
                                        opacity: 0.9,
                                    }}
                                >
                                    (
                                    {getFormattedPercentage(
                                        asset.roi?.percentage || 0,
                                        1
                                    )}
                                    )
                                </span>
                            </Title>
                        </div>
                        <div style={{ textAlign: "right" }}>
                            <Text
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Avg buy price
                            </Text>
                            <Title
                                level={5}
                                style={{
                                    color: Tokens.textPrimary,
                                    margin: "4px 0 0",
                                    fontSize: "15px",
                                    fontWeight: 500,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {getCurrencySymbol(currency)}
                                {formatSmartPrice(asset.averageBuyPrice || 0)}
                            </Title>
                        </div>
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "16px",
                            marginBottom: "12px",
                        }}
                    >
                        <div>
                            <Text
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Current price
                            </Text>
                            <div
                                data-testid={`asset-detail-price-${symbol}`}
                                style={{
                                    color: Tokens.textPrimary,
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                    marginTop: "2px",
                                }}
                            >
                                {getCurrencySymbol(currency)}
                                {formatSmartPrice(asset.currentPrice || 0)}
                            </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                            <Text
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Portfolio share
                            </Text>
                            <div
                                data-testid={`asset-portfolio-share-${symbol}`}
                                style={{
                                    color: Tokens.textPrimary,
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                    marginTop: "2px",
                                }}
                            >
                                {portfolioSharePct.toFixed(1)}%
                            </div>
                        </div>
                    </div>

                    {Math.abs(Number(asset.realizedPL) || 0) >= 0.01 && (
                        <div
                            data-testid={`asset-realized-pl-${symbol}`}
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "8px 10px",
                                marginBottom: "12px",
                                backgroundColor: Tokens.card,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusControl,
                            }}
                        >
                            <Text
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Realized P&amp;L
                            </Text>
                            <Text
                                style={{
                                    color:
                                        Number(asset.realizedPL) >= 0
                                            ? Tokens.positive
                                            : Tokens.negative,
                                    fontWeight: 500,
                                    fontSize: "13px",
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {Number(asset.realizedPL) >= 0 ? "+" : "-"}
                                {getCurrencySymbol(currency)}
                                {getFormattedAmount(
                                    Math.abs(Number(asset.realizedPL) || 0),
                                    true,
                                    2
                                )}
                            </Text>
                        </div>
                    )}

                    {allocationBadges.length > 0 && (
                        <div
                            style={{
                                display: "flex",
                                gap: "4px",
                                flexWrap: "wrap",
                                justifyContent: "flex-start",
                                marginTop: "8px",
                            }}
                        >
                            {allocationBadges.map((label) => (
                                <Tag
                                    key={label}
                                    style={{
                                        backgroundColor: Tokens.raised,
                                        color: Tokens.textSecondary,
                                        border: `1px solid ${Tokens.hairline}`,
                                        borderRadius: Tokens.radiusTag,
                                        fontSize: "11px",
                                        fontFamily: Tokens.fontMono,
                                        fontVariantNumeric: "tabular-nums",
                                        margin: 0,
                                        padding: "2px 8px",
                                    }}
                                >
                                    {label}
                                </Tag>
                            ))}
                        </div>
                    )}

                    <Divider
                        style={{
                            borderColor: Tokens.hairline,
                            margin: "16px 0 12px",
                        }}
                    />

                    <div
                        style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "center",
                            flexWrap: "wrap",
                        }}
                    >
                        <Button
                            type="default"
                            icon={
                                <MessageOutlined
                                    style={{ color: Tokens.textSecondary }}
                                />
                            }
                            onClick={() =>
                                history.push(
                                    `/imported/binance/ai-news?coin=${encodeURIComponent(
                                        symbol
                                    )}`
                                )
                            }
                            style={{
                                flex: 1,
                                backgroundColor: Tokens.raised,
                                color: Tokens.textPrimary,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusControl,
                                fontSize: "13px",
                                fontWeight: 500,
                            }}
                        >
                            Ask AI about {symbol}
                        </Button>
                        <Button
                            type="default"
                            icon={
                                <LineChartOutlined
                                    style={{ color: Tokens.textSecondary }}
                                />
                            }
                            onClick={handleShowMarketStats}
                            loading={marketLoading}
                            style={{
                                flex: 1,
                                backgroundColor: Tokens.raised,
                                color: Tokens.textPrimary,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusControl,
                                fontSize: "13px",
                                fontWeight: 500,
                            }}
                        >
                            Market stats
                        </Button>
                        <Button
                            type="default"
                            icon={
                                <HistoryOutlined
                                    style={{ color: Tokens.textSecondary }}
                                />
                            }
                            onClick={handleShowTransactions}
                            loading={transactionsLoading}
                            style={{
                                flex: 1,
                                backgroundColor: Tokens.raised,
                                color: Tokens.textPrimary,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusControl,
                                fontSize: "13px",
                                fontWeight: 500,
                            }}
                        >
                            View trades
                        </Button>
                    </div>

                    <MarketView
                        open={showMarketDrawer}
                        onClose={() => setShowMarketDrawer(false)}
                        showMarket={showMarketDrawer}
                        setShowMarket={setShowMarketDrawer}
                        coinInfo={marketCoinInfo}
                        currency={currency}
                    />

                    <BinanceTransactions
                        open={transactionsDrawerVisible}
                        onClose={() => setTransactionsDrawerVisible(false)}
                        asset={asset}
                        transactions={transactions}
                        loading={transactionsLoading}
                        pagination={{
                            ...transactionsPagination,
                            page: transactionsPage,
                        }}
                        onPageChange={handlePageChange}
                        onDaysChange={(newDays) =>
                            fetchTransactions(1, newDays)
                        }
                        days={activeDays}
                        cutoffDate={cutoffDate}
                        isMobile={isMobile}
                        isTablet={isTablet}
                        currency={currency}
                    />
                </div>
            </Panel>
        </Collapse>
    );
};

export default AssetInWallet;
